import Axios from "helpers/Interceptor";
import { CurrentBrand } from "modules/Types";

interface FacebookAdAccount {
    id: string;
    name: string;
    currency: string;
    account_status: string;
    amount_spent: string;
    business?: {
        id: string;
        name: string;
    };
}

interface FacebookInfo {
    fbAdAccount: FacebookAdAccount;
    loggedInFbUser: {
        access_token: string;
    };
}

export const getFacebookInformation_api = async (brandId: number): Promise<FacebookInfo | null> => {
    try {
        const response = await Axios.get<FacebookInfo>(`/admin/ad-account/${brandId}`);
        return response.data;
    } catch (error) {
        return null;
    }
}

export const getCampaingInformation_api = async (campaignId: number): Promise<any | null> => {
    try {
        const response = await Axios.get<any>(`/admin/campaign-info/${campaignId}`);
        return response.data;
    } catch (error) {
        return null;
    }
}

/** Interface for partner data */
export interface PartnerDiscountType {
    discountAmount?: number;
    partnerId: string;
    partnerName: string;
    cookieExpirationFromSignup?: number;
    startDate: string;
    appliesTo?: string;
}

/**
 * Fetches all affiliate partner keys from the admin API
 * @returns Promise resolving to an array of partner strings or null if the request fails
 */
export const getAffiliatePartners_api = async (): Promise<PartnerDiscountType[] | null> => {
    try {
        const response = await Axios.get<PartnerDiscountType[]>('/admin/get-affiliate-partners');
        return response.data;
    } catch (error) {
        return null;
    }
}

/** Response type for the affiliate partner snapshot API */
export interface AffiliatePartnerSnapshotResponse {
    users: {
        free: number;
        paid: number;
        overall: number;
        trialing: number;
        pro?: {
            annual?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
            monthly?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
        };
        lite?: {
            annual?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
            monthly?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
        };
    };
    payments: {
        successful: number;
        gross_revenue: number;
        net_revenue?: number;
        pro?: {
            annual?: {
                net: number;
                count: number;
                gross: number;
            };
            monthly?: {
                net: number;
                count: number;
                gross: number;
            };
        };
        lite?: {
            annual?: {
                net: number;
                count: number;
                gross: number;
            };
            monthly?: {
                net: number;
                count: number;
                gross: number;
            };
        };
    };
    campaigns: {
        totalSpend: number;
        draftCampaigns: number;
        totalCampaigns: number;
        nonDraftCampaigns: number;
    };
    totalSignups: number;
    trialConversion: {
        started: number;
        canceled: number;
        trialing: number;
        converted: number;
        payment_failure_cancellation: number;
        pro?: {
            annual?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
            monthly?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
        };
        lite?: {
            annual?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
            monthly?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
        };
    };
}

/**
 * Fetches snapshot data for a specific affiliate partner
 * @param partnerId - The unique identifier for the partner
 * @returns Promise resolving to the snapshot data or null if the request fails
 */
export const getAffiliatePartnerSnapshot_api = async (partnerId: string): Promise<AffiliatePartnerSnapshotResponse | null> => {
    try {
        const response = await Axios.post<{snapshot: AffiliatePartnerSnapshotResponse}>('/admin/get-affiliate-partner-snapshot', { partnerId });
        return response.data.snapshot;
    } catch (error) {
        return null;
    }
}

/**
 * Updates a brand's subscription plan and trial settings via admin API
 * 
 * @param params - Configuration for the subscription update
 * @param params.planType - The plan type to set for the brand
 * @param params.trialDays - Optional number of trial days if creating a trial
 * @param params.userEmail - The email of the user to update the subscription for
 * @param params.brandId - The unique identifier for the brand
 * @returns Promise resolving to the API response data
 */
export const createTrialForBrand = async (
    {
        planType,
        trialDays = 14,
        userEmail,
        brandId
    }: {
        planType: string;
        trialDays?: number;
        userEmail: string;
        brandId: number,
    }
): Promise<any> => {
    const response = await Axios.post<CurrentBrand>(`/admin/create-trial-subscription`, {
        planType,
        trialDays,
        userEmail,
        brandId
    });
    return response.data;
};

/**
 * Fetches top performing brands data for admin dashboard
 * 
 * @param params - Query parameters for filtering and pagination
 * @param params.limit - Maximum number of results to return
 * @param params.offset - Number of results to skip (for pagination)
 * @param params.timeRange - Time period to analyze ('7d', '14d', '30d', 'custom')
 * @param params.startDate - Optional start date (only required for custom time range)
 * @param params.endDate - Optional end date (only required for custom time range)
 * @param params.type - Optional event type to filter by ('Page View', 'StreamingLinkClicked', etc.)
 * @returns Promise resolving to the top performing brands data
 */
export const getTopPerformingBrands_api = async (
  params: {
    limit: number;
    offset: number;
    timeRange: string;
    startDate?: string;
    endDate?: string;
    type?: string;
  }
): Promise<any> => {
  try {
    const response = await Axios.get('/admin/top-performing', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching top performing brands:', error);
    throw error;
  }
};

/**
 * Processes a video to generate clips at specified intervals
 * 
 * @param params - Processing parameters
 * @param params.videoUrl - The URL of the video to process
 * @param params.videoId - The Cloudinary public_id of the video
 * @param params.clipInterval - Time interval in seconds for each clip
 * @param params.clipLength - Optional length/duration in seconds for each clip
 * @returns Promise resolving to the generated clips data
 */
export const processVideo_api = async (
  params: {
    videoUrl: string;
    videoId: string;
    clipInterval: number;
    clipLength?: number;
  }
): Promise<{
  success: boolean;
  clips: Array<{
    id: string;
    url: string;
    thumbnailUrl: string;
    startTime: number;
    endTime: number;
    duration: number;
  }>;
}> => {
  try {
    const response = await Axios.post('/admin/process-video', params);
    return response.data;
  } catch (error) {
    console.error('Error processing video:', error);
    throw error;
  }
};

/** Interface for top playbooks data */
export interface TopPlaybook {
  playbookType: string;
  campaignCount: number;
  totalBudgetUsd: number;
  avgBudgetUsd: number;
}

/** Interface for median campaigns per brand data */
export interface MedianCampaignsPerBrand {
  playbookType: string;
  medianCampaignsPerBrand: number;
}

/** Interface for monthly breakdown data */
export interface MonthlyBreakdown {
  monthStartDate: Date;
  campaigns: number;
  uniqueBrands: number;
  totalBudgetUsd: number;
  avgSpendPerBrand: number;
}

/** Interface for high level median stats data */
export interface HighLevelMedianStats {
  avgSpendPerCampaign: number;
  medianSpendPerCampaign: number;
  minSpendPerCampaign: number;
  maxSpendPerCampaign: number;
  avgCampaignsPerArtistPerMonth: number;
  avgTotalMonthlySpendPerArtist: number;
}

/** Response type for the affiliate partner upgraded stats API */
export interface AffiliatePartnerUpgradedStatsResponse {
  partnerId: string;
  topPlaybooks: TopPlaybook[];
  medianCampaignsPerBrand: MedianCampaignsPerBrand[];
  monthlyBreakdown: MonthlyBreakdown[];
  highLevelMedianStats: HighLevelMedianStats | null;
  executionTimeMs: number;
  success: boolean;
}

/**
 * Fetches upgraded statistics for a specific affiliate partner
 * @param partnerId - The unique identifier for the partner
 * @returns Promise resolving to the upgraded stats data or null if the request fails
 */
export const getAffiliatePartnerUpgradedStats_api = async (partnerId: string): Promise<AffiliatePartnerUpgradedStatsResponse | null> => {
    try {
        const response = await Axios.post<AffiliatePartnerUpgradedStatsResponse>('/admin/affiliate-partner-upgraded-stats', { partnerId });
        return response.data;
    } catch (error) {
        console.error('Error fetching affiliate partner upgraded stats:', error);
        return null;
    }
};

/** Interface for manual tier update request parameters */
export interface ManualTierUpdateRequest {
    brandId: number;
    tierValue: 'starter' | 'growth';
    billingCycle: 'monthly' | 'annual';
}

/** Interface for manual tier update response */
export interface ManualTierUpdateResponse {
    brandId: number;
    oldTier: string;
    newTier: string;
    billingCycle: string;
    brandName: string;
    message: string;
}

/**
 * Manually updates a brand's subscription tier and billing cycle
 * @param params - The parameters for manual tier update
 * @param params.brandId - The ID of the brand to update
 * @param params.tierValue - The tier to update to ('starter' or 'growth')
 * @param params.billingCycle - The billing cycle ('monthly' or 'annual')
 * @returns Promise resolving to the update result or null if the request fails
 */
export const updateBrandTierManually_api = async ({
    brandId,
    tierValue,
    billingCycle
}: ManualTierUpdateRequest): Promise<ManualTierUpdateResponse | null> => {
    try {
        const response = await Axios.post<ManualTierUpdateResponse>('/admin/manual-tier-update', {
            brandId,
            tierValue,
            billingCycle
        });
        return response.data;
    } catch (error) {
        console.error('Error updating brand tier manually:', error);
        return null;
    }
};

/** Interface for agent campaign data */
export interface AdminAgentCampaign {
    id: number;
    brand_id: number;
    status: string;
    playbook_type: string;
    platform: string;
    campaign_metadata: any;
    agent_notes: any;
    marketing_plan_id: string | null;
    chat_id: string | null;
    created_by_user_id: number | null;
    public_tracking_token: string | null;
    createdAt: string;
    updatedAt: string;
    brand: {
        id: number;
        name: string;
        slug: string;
        tier: string;
        stripe_customer_id?: string;
        user_emails: string[];
    };
    facebook_connections: {
        logged_in_fb_user: any;
        facebook_ad_account: any;
        facebook_page: any;
        instagram_page: any;
    };
    is_express_campaign: boolean;
}

/** Payment intent interface */
export interface PaymentIntent {
    id: string;
    type: string;
    amount_cents: number;
    currency: string;
    status: string;
    stripe_customer_id: string;
    metadata: any;
    created_at: string;
}

/** Extended campaign interface for all campaigns (includes additional fields) */
export interface AdminAllCampaign extends AdminAgentCampaign {
    is_agent_campaign: boolean;
    payment_intents?: PaymentIntent[];
    airtable_status?: {
        id: string;
        status: string;
        refund_url?: string;
        refunded?: boolean;
        cancelled_reason?: string;
        start_date?: string;
        end_date?: string;
    } | null;
}

/** Response interface for admin all campaigns API */
export interface AdminAllCampaignsResponse {
    campaigns: AdminAllCampaign[];
    total: number;
    limit: number;
    offset: number;
}

/** Parameters for fetching all campaigns */
export interface GetAdminAllCampaignsParams {
    campaignId?: number;
    brandId?: number;
    playbook_type?: string;
    ad_platform?: string;
    status?: string;
    createdAtAfter?: string;
    createdAtBefore?: string;
    limit?: number;
    offset?: number;
}

/** Response interface for admin agent campaigns API */
export interface AdminAgentCampaignsResponse {
    campaigns: AdminAgentCampaign[];
    total: number;
    limit: number;
    offset: number;
}

/** Parameters for fetching admin agent campaigns */
export interface GetAdminAgentCampaignsParams {
    campaignId?: number;
    brandId?: number;
    playbook_type?: string;
    ad_platform?: string;
    status?: string;
    createdAtAfter?: string;
    createdAtBefore?: string;
    limit?: number;
    offset?: number;
}

/**
 * Fetches agent campaigns with filtering options
 * @param params Filtering parameters
 * @returns Promise resolving to agent campaigns data or null if the request fails
 */
export const getAdminAgentCampaigns_api = async (params: GetAdminAgentCampaignsParams = {}): Promise<AdminAgentCampaignsResponse | null> => {
    try {
        const queryParams = new URLSearchParams();
        
        if (params.campaignId) queryParams.append('campaignId', params.campaignId.toString());
        if (params.brandId) queryParams.append('brandId', params.brandId.toString());
        if (params.playbook_type) queryParams.append('playbook_type', params.playbook_type);
        if (params.ad_platform) queryParams.append('ad_platform', params.ad_platform);
        if (params.status) queryParams.append('status', params.status);
        if (params.createdAtAfter) queryParams.append('createdAtAfter', params.createdAtAfter);
        if (params.createdAtBefore) queryParams.append('createdAtBefore', params.createdAtBefore);
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.offset) queryParams.append('offset', params.offset.toString());

        const response = await Axios.get<AdminAgentCampaignsResponse>(`/admin/agent-campaigns?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching agent campaigns:', error);
        return null;
    }
};

/**
 * Fetches ALL campaigns (not just agent campaigns) with filtering options
 * @param params Filtering parameters
 * @returns Promise resolving to all campaigns data or null if the request fails
 */
export const getAdminAllCampaigns_api = async (params: GetAdminAllCampaignsParams = {}): Promise<AdminAllCampaignsResponse | null> => {
    try {
        const queryParams = new URLSearchParams();
        
        if (params.campaignId) queryParams.append('campaignId', params.campaignId.toString());
        if (params.brandId) queryParams.append('brandId', params.brandId.toString());
        if (params.playbook_type) queryParams.append('playbook_type', params.playbook_type);
        if (params.ad_platform) queryParams.append('ad_platform', params.ad_platform);
        if (params.status) queryParams.append('status', params.status);
        if (params.createdAtAfter) queryParams.append('createdAtAfter', params.createdAtAfter);
        if (params.createdAtBefore) queryParams.append('createdAtBefore', params.createdAtBefore);
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.offset) queryParams.append('offset', params.offset.toString());

        const response = await Axios.get<AdminAllCampaignsResponse>(`/admin/all-campaigns?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching all campaigns:', error);
        return null;
    }
};

/** Parameters for refunding YouTube campaign */
export interface RefundYoutubeCampaignParams {
    campaignId: number;
}

/** Response interface for YouTube campaign refund */
export interface RefundYoutubeCampaignResponse {
    campaignId: number;
    airtableRecordId: string;
    refundDetails: Array<{
        paymentIntentId: string;
        refundId?: string;
        amount?: string;
        currency?: string;
        status?: string;
        source: string;
        error?: string;
    }>;
    successfulRefunds: number;
    failedRefunds: number; 
    message: string;
}

/**
 * Refunds a YouTube campaign by processing Stripe payment refunds and updating Airtable
 * @param params Refund parameters
 * @returns Promise resolving to refund response or null if the request fails
 */
export const refundYoutubeCampaign_api = async (params: RefundYoutubeCampaignParams): Promise<RefundYoutubeCampaignResponse | null> => {
    try {
        const response = await Axios.post<RefundYoutubeCampaignResponse>('/admin/refund-youtube-campaign', params);
        return response.data;
    } catch (error) {
        console.error('Error refunding YouTube campaign:', error);
        return null;
    }
};

/** Parameters for syncing Stripe subscriptions */
export interface SyncStripeSubscriptionsParams {
    brandId?: number;
    stripe_subscription_id?: string;
    dryRun?: boolean;
}

/** Updated brand information from sync response */
export interface SyncedBrandInfo {
    brandId: number;
    brandName?: string;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    currentBrandTier?: string;
    desiredBrandTierPostUpdate?: string;
    wouldUpdate?: boolean;
    reason?: string;
    oldTier: string;
    newTier: string;
    recurringInterval: string;
}

/** Response interface for Stripe subscription sync */
export interface SyncStripeSubscriptionsResponse {
    isDryRun: boolean;
    summary: {
        totalSubscriptionsProcessed: number;
        brandsExamined: number;
        brandsUpdated: number;
        errorsEncountered: number;
    };
    updatedBrands: SyncedBrandInfo[];
    errors: Array<{ brandId?: number; error: string }>;
    filteringApplied: {
        brandId: number | null;
        stripeSubscriptionId: string | null;
        dryRun: boolean;
    };
    message: string;
}

/**
 * Syncs Stripe subscriptions with database
 * @param params Parameters for sync operation
 * @returns Promise resolving to sync results or null if the request fails
 */
export const syncStripeSubscriptions_api = async (params: SyncStripeSubscriptionsParams = {}): Promise<SyncStripeSubscriptionsResponse | null> => {
    try {
        const queryParams = new URLSearchParams();
        
        if (params.brandId) queryParams.append('brandId', params.brandId.toString());
        if (params.stripe_subscription_id) queryParams.append('stripe_subscription_id', params.stripe_subscription_id);
        if (params.dryRun !== undefined) queryParams.append('dryRun', params.dryRun ? '1' : '0');

        const response = await Axios.post<SyncStripeSubscriptionsResponse>(`/admin/sync-stripe-subscriptions?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Error syncing Stripe subscriptions:', error);
        return null;
    }
};

/**
 * Bulk process multiple agent campaigns
 */
export const bulkProcessAgentCampaigns_api = async (campaignIds: number[]): Promise<{
    successCount: number;
    errorCount: number;
}> => {
    try {
        const response = await Axios.post('/admin/agent-campaigns/bulk-process', {
            campaignIds
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk processing agent campaigns:', error);
        throw error;
    }
};

/**
 * Bulk update campaign status
 */
export const bulkUpdateCampaignStatus_api = async (campaignIds: number[], status: string): Promise<{
    successCount: number;
    errorCount: number;
}> => {
    try {
        const response = await Axios.post('/admin/agent-campaigns/bulk-update-status', {
            campaignIds,
            status
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk updating campaign status:', error);
        throw error;
    }
};

/**
 * Parameters for reconcile brand invoices API call
 */
export interface ReconcileBrandInvoicesParams {
    brandId?: number;
    userId?: number;
    email?: string;
    brandName?: string;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
    exportCsv?: boolean;
}

/**
 * Invoice record interface
 */
export interface InvoiceRecord {
    brandId: number;
    campaignId: number | null;
    paymentPlatform: 'stripe' | 'paypal';
    invoiceLink: string | null;
    totalAmount: number;
    currency: string;
    paymentDate: string;
    campaignName?: string;
    campaignPlaybook?: string;
    brandName?: string;
}

/**
 * Response interface for reconcile brand invoices
 */
export interface ReconcileBrandInvoicesResponse {
    invoices: InvoiceRecord[];
    brand?: {
        id: number;
        name: string;
        stripe_customer_id?: string;
    };
    processedBrands?: Array<{
        id: number;
        name: string;
        invoiceCount: number;
        orphanedCampaignsCount?: number;
    }>;
    summary: {
        searchMethod: 'brand' | 'user' | 'email';
        searchValue: string | number;
        totalBrandsProcessed?: number;
        totalInvoicesFound: number;
        totalOrphanedCampaigns: number;
        totalRecordsReturned: number;
        averageInvoicesPerBrand?: number;
        averageOrphanedCampaignsPerBrand?: number;
    };
    message?: string;
}

/**
 * Reconciles brand invoices across PayPal and Stripe payment methods
 * @param params Parameters for invoice reconciliation
 * @returns Promise resolving to invoice reconciliation results or null if the request fails
 */
export const reconcileBrandInvoices_api = async (params: ReconcileBrandInvoicesParams): Promise<ReconcileBrandInvoicesResponse | null> => {
    try {
        const response = await Axios.post<ReconcileBrandInvoicesResponse>('/admin/reconcile-brand-invoices', params);
        return response.data;
    } catch (error) {
        console.error('Error reconciling brand invoices:', error);
        return null;
    }
};

/**
 * Downloads CSV export of reconciled brand invoices
 * @param params Parameters for invoice reconciliation (with exportCsv set to true)
 * @returns Promise resolving to CSV blob download
 */
export const downloadInvoicesCsv_api = async (params: ReconcileBrandInvoicesParams): Promise<void> => {
    try {
        const csvParams = { ...params, exportCsv: true };
        const response = await Axios.post('/admin/reconcile-brand-invoices', csvParams, {
            responseType: 'blob'
        });

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename
        const timestamp = new Date().toISOString().slice(0, 10);
        const searchType = params.brandId ? `brand-${params.brandId}` :
                          params.userId ? `user-${params.userId}` :
                          params.email ? `email-${params.email.replace(/[^a-zA-Z0-9]/g, '-')}` :
                          'all';
        const filename = `invoice-report-${searchType}-${timestamp}.csv`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading invoices CSV:', error);
        throw error;
    }
};
